/**
 * GlobalContactListener.js
 *
 * ES6 Module managing global click events for contact buttons, SSR/CSR compatible.
 *
 * Responsibilities:
 * - Singleton listener delegating clicks on `.contact-us-btn` or `[data-contact-trigger]`
 * - Debounces rapid clicks and prevents concurrent processing
 * - Exposes methods to setup, remove, reset, destroy, and inspect state
 * - Tracks essential errors via ErrorHandler
 *
 * SSR Behavior:
 * - No-op stub in server-side environments
 * - Hydration support on client-side
 *
 * Features:
 * - Async-safe dynamic import removed; always available via ESM import
 * - Named exports for flexibility
 * - Default export of a singleton instance
 *
 * Limitations:
 * - Assumes browser DOM APIs present for client-side
 * - Error tracking minimal; only critical failures recorded
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

const isServerSide = typeof window === 'undefined' || typeof document === 'undefined';
const isClientSide = !isServerSide;

class GlobalContactListenerSSRStub {
  constructor() {
    this.isListening = false;
    this.currentHandler = null;
    this.isProcessing = false;
    this.lastClickTime = 0;
    this.debounceDelay = 300;
    console.log('🖥️ GlobalContactListener: SSR mode (no-op)');
  }

  setupListener(handler) {
    this.currentHandler = handler;
    console.log('🖥️ SSR setupListener (no-op)');
  }
  handleGlobalClick() {}
  removeListener() {}
  isActive() { return false; }
  getProcessingState() { return false; }
  reset() {}
  getRecentErrors() { return []; }
  hasCriticalIssues() { return false; }
  destroy() {}
  getDebugInfo() {
    return {
      ssrMode: true,
      isListening: false,
      isProcessing: false,
      lastClickTime: 0,
      debounceDelay: this.debounceDelay
    };
  }
}

class GlobalContactListenerClient {
  constructor() {
    this.isListening = false;
    this.currentHandler = null;
    this.isProcessing = false;
    this.lastClickTime = 0;
    this.debounceDelay = 300;
    this._errorHandler = new ErrorHandler({ module: 'GlobalContactListener', maxErrors: 15 });
  }

  setupListener(handler) {
    if (this.isListening) this.removeListener();
    if (!document?.body) {
      this._errorHandler.handleFatalError('Document body unavailable');
      return;
    }
    this.currentHandler = e => this.handleGlobalClick(e, handler);
    document.body.addEventListener('click', this.currentHandler, { passive: false });
    this.isListening = true;
    console.log('✅ Global contact listener established');
  }

  handleGlobalClick(e, handler) {
    try {
      const btn = e.target.closest('.contact-us-btn, [data-contact-trigger]');
      if (!btn) return;
      e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
      const now = Date.now();
      if (now - this.lastClickTime < this.debounceDelay) return;
      this.lastClickTime = now;
      if (this.isProcessing) return;
      this.isProcessing = true;
      handler(btn);
    } catch (err) {
      this._errorHandler.handleError('Error processing click', err);
    } finally {
      setTimeout(() => { this.isProcessing = false; }, 1000);
    }
  }

  removeListener() {
    if (this.isListening && this.currentHandler) {
      document.body.removeEventListener('click', this.currentHandler);
      this.currentHandler = null;
      this.isListening = false;
    }
  }

  reset() {
    this.isProcessing = false;
    this.lastClickTime = 0;
  }

  isActive() { return this.isListening; }
  getProcessingState() { return this.isProcessing; }
  getRecentErrors(minutes) { return this._errorHandler.getRecentErrors(minutes); }
  hasCriticalIssues() { return this._errorHandler.hasFatalErrors(); }

  getDebugInfo() {
    return {
      ssrMode: false,
      isListening: this.isListening,
      isProcessing: this.isProcessing,
      lastClickTime: this.lastClickTime,
      debounceDelay: this.debounceDelay,
      hasCriticalIssues: this.hasCriticalIssues()
    };
  }

  destroy() {
    this.removeListener();
    this.reset();
  }
}

// Singleton export
const GlobalContactListener = isServerSide
  ? new GlobalContactListenerSSRStub()
  : new GlobalContactListenerClient();

// Hydration helper
GlobalContactListener.hydrate = function() {
  if (isClientSide && this instanceof GlobalContactListenerSSRStub) {
    const client = new GlobalContactListenerClient();
    if (this.currentHandler) client.setupListener(this.currentHandler);
    return client;
  }
  return this;
};

export default GlobalContactListener;
export { GlobalContactListenerClient as Client, GlobalContactListenerSSRStub as SSRStub };